import type { Field } from "../lib/api";
import { DOC_TYPE_LABELS } from "../lib/api";
import { fieldLabel, pageLabel, percent } from "../lib/words";

/**
 * One field of the asset record.
 *
 * Four states are distinguished deliberately, because collapsing them is how a document system
 * misleads people:
 *   several documents state the same value          -> the strongest evidence available
 *   one document states it and the readers agree    -> usable
 *   documents disagree, or the region was a guess   -> needs a person
 *   "not stated in source", which is an answer, not a failure
 *
 * The source column names the document type the value came from, because that is why it was
 * preferred: a serial number comes off the test certificate for that unit, a part number off the
 * drawing that carries the part list.
 */
export function FieldRow({ field, onCite }: { field: Field; onCite: (f: Field) => void }) {
  const p = field.provenance;
  const corroborated = field.corroborating_sources > 1;

  return (
    <tr>
      <td style={{ width: 180 }}>{fieldLabel(field.name)}</td>
      <td>
        {field.not_stated ? (
          <span className="muted">Not stated in source</span>
        ) : field.value ? (
          <span className="mono">{field.value}</span>
        ) : (
          <span className="muted">Could not be read</span>
        )}
        {field.in_conflict && (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {field.alternatives
              .filter((a) => a.value !== field.value)
              .map((a) => `${a.value} in the ${DOC_TYPE_LABELS[a.doc_type] ?? a.doc_type}`)
              .join(", ")}
          </div>
        )}
      </td>
      <td style={{ width: 100 }}>
        {!field.not_stated && <span className="mono">{percent(field.confidence)}</span>}
      </td>
      <td style={{ width: 250 }}>
        {field.in_conflict && <span className="pill review">documents disagree</span>}{" "}
        {corroborated && !field.in_conflict && (
          <span className="pill ok">{field.corroborating_sources} sources agree</span>
        )}{" "}
        {p.agreement === false && <span className="pill review">readers disagree</span>}{" "}
        {p.ambiguous && <span className="pill review">region ambiguous</span>}{" "}
        {field.value && !p.box && <span className="pill review">not marked on the page</span>}{" "}
        {!field.needs_review && field.value && !corroborated && (
          <span className="pill ok">confirmed</span>
        )}
      </td>
      <td style={{ width: 170 }}>
        {field.chosen_from && (
          <div className="muted" style={{ fontSize: 12 }}>
            {DOC_TYPE_LABELS[field.chosen_from] ?? field.chosen_from.replace(/_/g, " ")}
          </div>
        )}
        {/* A page with no located region still opens: a born-digital PDF gives its page and no
            box, and the page is the evidence. The viewer says the place is not marked. */}
        {p.page_number ? (
          <button className="ghost" onClick={() => onCite(field)}
                  title={p.box ? undefined : "Opens the page; the value's place on it is not marked"}>
            {pageLabel(p.page_number)}
          </button>
        ) : field.value ? (
          <span className="muted">{pageLabel(p.page_number)}</span>
        ) : null}
      </td>
    </tr>
  );
}
